import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SERVICES, CONTACT, ServiceData } from "@/lib/constants";
import { MessageCircle, Send, ChevronRight, CheckCircle, Clock } from "lucide-react";
import { apiFetch } from "@/lib/api";

interface ServicePageProps {
  serviceId: string;
}

/* @section: service-page-template */
export default function ServicePage({ serviceId }: ServicePageProps) {
  const service = SERVICES.find((s) => s.id === serviceId)!;
  const [images, setImages] = useState<Record<string, string>>({});
  const [content, setContent] = useState<Record<string, string>>({});
  const [sections, setSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    apiFetch("/content/images").then((r) => r.json()).then((d) => setImages(d.data ?? {})).catch(() => {});
    apiFetch("/content/text").then((r) => r.json()).then((d) => setContent(d.data ?? {})).catch(() => {});
    apiFetch("/content/sections").then((r) => r.json()).then((d) => setSections(d.data ?? {})).catch(() => {});
    window.scrollTo(0, 0);
  }, [serviceId]);

  const illustration = images[`illus_${serviceId}`] ?? service.illustration;
  const icon = images[`icon_${serviceId}`] ?? service.icon;
  const description = content[`${serviceId}_description`] ?? service.description;
  const subtitle = content[`${serviceId}_subtitle`] ?? service.subtitle;

  const otherServices = SERVICES.filter((s) => s.id !== serviceId);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-16 bg-[#2E8B57] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl overflow-hidden mb-6 border-2 border-[#FFE000]/50 bg-[#D4EDDA]">
              <img src={icon} alt={service.name} className="w-full h-full object-cover" style={{ mixBlendMode: "multiply" }} />
            </div>
            <div className="w-12 h-1 mb-4" style={{ backgroundColor: service.color }} />
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{service.name}</h1>
            <p className="text-[#FFE000] font-medium text-lg mb-6">{subtitle}</p>
            <p className="text-gray-300 leading-relaxed text-lg mb-8">{description}</p>

            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-400 text-sm bg-white/5 px-4 py-2 rounded-full">
                <Clock size={16} className="text-[#FFE000]" />
                {service.duration}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#1ebe5b] transition-all hover:scale-105 shadow-lg">
                <MessageCircle size={18} /> WhatsApp 預約
              </a>
              <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 px-6 py-3 bg-[#2AABEE] text-white font-semibold rounded-full hover:bg-[#1a9fd4] transition-all hover:scale-105 shadow-lg">
                <Send size={18} /> Telegram 預約
              </a>
            </div>
          </div>

          {/* Illustration with floating animation */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl blur-3xl opacity-20" style={{ backgroundColor: service.color }} />
              <img
                src={illustration}
                alt={service.name}
                className="relative rounded-3xl shadow-2xl w-full max-w-lg border border-white/10"
                style={{ animation: "float 6s ease-in-out infinite" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      {!sections[`${serviceId}_benefits`] && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#2E8B57] mb-3">
                {content[`${serviceId}_benefits_title`] ?? "主要保健功效"}
              </h2>
              <div className="w-12 h-1 mx-auto" style={{ backgroundColor: service.color }} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {service.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color: service.color }} />
                  <span className="text-gray-700 font-medium">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Booking CTA ── */}
      {!sections[`${serviceId}_cta`] && (
        <section className="py-16 bg-[#2E8B57]">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {content[`${serviceId}_cta_title`] ?? `立即預約${service.name}`}
            </h2>
            <p className="text-gray-400 mb-8">
              {content[`${serviceId}_cta_subtitle`] ?? "透過 WhatsApp 或 Telegram 聯絡我們安排預約時間"}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-center gap-2 px-8 py-3 bg-[#25D366] text-white font-bold rounded-full hover:bg-[#1ebe5b] transition-all">
                <MessageCircle size={18} /> WhatsApp 預約
              </a>
              <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-center gap-2 px-8 py-3 bg-[#2AABEE] text-white font-bold rounded-full hover:bg-[#1a9fd4] transition-all">
                <Send size={18} /> Telegram 預約
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ── Other Services ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#2E8B57] mb-8 text-center">其他服務項目</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {otherServices.map((s) => (
              <Link key={s.id} to={s.slug}
                className="group text-center bg-gray-50 rounded-xl p-4 hover:bg-[#2E8B57] transition-colors border border-gray-100 hover:border-[#FFE000]/30">
                <img src={images[`icon_${s.id}`] ?? s.icon} alt={s.name}
                  className="w-14 h-14 mx-auto rounded-full object-cover mb-3 border-2 border-gray-200 group-hover:border-[#FFE000]/50"
                  style={{ mixBlendMode: "multiply" }} />
                <p className="text-sm font-medium text-gray-700 group-hover:text-[#FFE000] transition-colors">{s.name}</p>
                <span className="text-xs text-gray-400 group-hover:text-gray-300 flex items-center justify-center gap-1 mt-1">
                  了解更多 <ChevronRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </div>
  );
}
